import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Form, Button, FormControl, InputGroup, Modal, Table, Row, Col } from 'react-bootstrap';
import { FaTimes, FaCheck, FaPlus } from 'react-icons/fa';
import { getAllPersons } from '../../services/Person/personService';
import PaginationComponent from '../Pagination/PaginationComponent';
import CreatePersonModal from '../Person/CreatePersonModal';

const PersonInput = ({ value, onChange, label, isInvalid, userRoles, createdNew, type }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(value || { id: null, name: "", discount: null });
  const [searchText, setSearchText] = useState(value?.name || "");
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [persons, setPersons] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const previousFilterParams = useRef({});
  const [filterParams, setFilterParams] = useState({});
  const [shouldFetch, setShouldFetch] = useState(false);

  useEffect(() => {
    setSelectedPerson(value || { id: null, name: "", discount: null });
    setSearchText(value?.name || "");
    setIsReadOnly(!!value?.id);
  }, [value]);

  const fetchPersons = useCallback(async () => {
    if (!shouldFetch) return;

    try {
      const data = await getAllPersons(filterParams, page - 1, pageSize);
      setPersons(data.content);
      setTotalPages(data.totalPages);
    } catch (error) {
      console.error("Erro ao buscar pessoas:", error);
    }
  }, [shouldFetch, filterParams, page, pageSize]);

  useEffect(() => {
    if (shouldFetch) {
      fetchPersons();
    }
  }, [page, pageSize, filterParams, fetchPersons, shouldFetch]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const filter = { name: searchText, type: type };
      handleFilter(filter);
      setShowModal(true);
    }
  };

  const handleSelectPerson = (person) => {
    setSelectedPerson(person);
    setSearchText(person.name);
    setIsReadOnly(true);
    onChange({ name: person.oldId + ' - ' + person.name, id: person.id, discount: person.discount });
    setShowModal(false);
    setShouldFetch(false);
  };

  const handleClear = () => {
    setSelectedPerson({ name: "", id: null, discount: null });
    setSearchText("");
    setIsReadOnly(false);
    onChange(null);
  };

  const handleFilter = (filters) => {
    const filtersChanged = JSON.stringify(previousFilterParams.current) !== JSON.stringify(filters);
  
    if (filtersChanged) {
      const isOnlyNumbers = /^\d+$/.test(filters.name);
  
      const formattedFilters = {
        ...mapPersonTypes(filters.type),
        ...(isOnlyNumbers ? { oldId: filters.name } : { name: filters.name }),
        active: true
      };
  
      setFilterParams(formattedFilters);
      setPage(1);
      previousFilterParams.current = formattedFilters;
      setShouldFetch(true);
    }
  };  

  const handleCloseModal = () => {
    if (!selectedPerson.id) {
      setSearchText("");
    }
    setShowModal(false);
    setShouldFetch(false);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setShouldFetch(true);
  };

  const mapPersonTypes = (types) => {
    return {
      includeCustomer: types?.includes("isCustomer") || false,
      includeSupplier: types?.includes("isSupplier") || false,
      includeDriver: types?.includes("isDriver") || false,
      includeProducer: types?.includes("isProducer") || false
    }; 
  };  

  return (
    <>
      <div className="mb-3">
        {label && <Form.Label className="mb-1">{label}</Form.Label>}
        <InputGroup className={isInvalid ? "is-invalid" : ""}>
          <FormControl
            placeholder="Selecione uma pessoa"
            value={searchText}
            isInvalid={isInvalid}
            readOnly={isReadOnly}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={handleKeyDown}
            autoComplete='off'
          />
          <Button variant="outline-secondary" onClick={handleClear} disabled={userRoles}>
            <FaTimes />
          </Button>
        </InputGroup>
        {isInvalid && <div className="invalid-feedback">{label} é obrigatório.</div>}
      </div>

      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Selecionar Pessoa</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ textAlign: "center", display: createdNew ? "block" : "none" }} className="mb-3">
            <Button
              variant="primary"
              className="button-custom mt-0"
              onClick={(e) => {
                e.stopPropagation();
                setShowModal(false);
                setShowCreateModal(true);
              }}
            >
              <FaPlus className="me-2" />
              Novo
            </Button>
          </div>
          <Table striped bordered hover responsive="sm" className="mt-3">
            <thead>
              <tr>
                <th>Código - Nome</th>
                <th>Documento</th>
                <th style={{ width: "50px" }}>Ação</th>
              </tr>
            </thead>
            <tbody>
              {persons.map((person) => (
                <tr key={person.id}>
                  <td>{person.oldId + ' - ' + person.name}</td>
                  <td>{person.document}</td>
                  <td>
                    <Button
                      variant="outline-primary"
                      onClick={() => handleSelectPerson(person)}
                      className="btn-primary button-custom d-flex align-items-center justify-content-center p-1"
                      style={{ borderRadius: "4px", padding: "5px 10px", minWidth: "40px", height: "30px" }}
                    >
                      <FaCheck style={{ color: "inherit", opacity: 0.6 }} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Row className="mt-3">
            <Col>
              <PaginationComponent
                totalPages={totalPages}
                currentPage={page}
                onPageChange={handlePageChange}
                totalElements={totalPages * pageSize}
                size={pageSize}
                pageNumber={page - 1}
                numberOfElements={persons.length}
              />
            </Col>
          </Row>
        </Modal.Body>
      </Modal>
      <CreatePersonModal
        show={showCreateModal}
        handleClose={() => setShowCreateModal(false)}
        onCreated={(created) => {
          setShowCreateModal(false);
          setSelectedPerson(created);
          onChange(created);
        }}
      />
    </>
  );
};

export default PersonInput;